import type { ChatWindowTheme } from '@lockton-companies/locktonre-shared-web-components/chat-window/angular'

/**
 * SAGE dark theme for the AI chat window.
 *
 * Maps directly to the CSS custom properties defined in
 * src/styles/theme-dark.scss (.app-theme-dark) so the chat window
 * is visually consistent with the rest of the SAGE platform.
 *
 * Accent colour: teal rgb(0, 179, 170) — SAGE's --accent token.
 */
export const sageTheme: ChatWindowTheme = {
  '--chat-window-max-width': 'none',
  '--chat-window-min-width': '280px',

  // Backgrounds — mirrors --bg → --bg-1 → --bg-2 → --bg-3
  '--color-bg-1': 'rgb(0, 0, 0)', // Deepest layer (--bg)
  '--color-bg-2': 'rgb(22, 26, 27)', // Assistant bubbles (--bg-1)
  '--color-bg-3': 'rgb(36, 40, 41)', // Input field (--bg-2)
  '--color-bg-hover': 'rgb(55, 64, 66)', // Hover state (--bg-3)

  // Borders
  '--color-border-1': 'rgb(37, 44, 45)', // Subtle dividers (--border-1)
  '--color-border-2': 'rgb(0, 179, 170)', // Focus/active border (--accent)

  // Accent — SAGE teal
  '--color-accent': 'rgb(0, 179, 170)',
  '--color-accent-lit': 'rgb(4, 214, 203)',
  '--color-accent-hint': 'rgba(0, 179, 170, 0.12)',
  '--color-accent-spinner-bg': 'rgba(0, 179, 170, 0.2)',
  '--color-bg-accent': 'rgb(0, 88, 80)', // User bubble bg (--accent-hint)
  '--color-bg-accent-subtle': 'rgb(0, 51, 48)', // --bg-accent

  // Text
  '--color-body': 'rgb(255, 255, 255)', // --body
  '--color-body-inverse': 'rgb(0, 0, 0)',
  '--color-primary': 'rgb(204, 226, 242)', // --primary-lit
  '--color-primary-lit': 'rgb(255, 255, 255)',
  '--color-subtle': 'rgb(116, 116, 116)', // --subtle
  '--color-hint': 'rgb(71, 75, 79)', // --hint

  // Warn
  '--color-warn': 'rgb(241, 70, 47)',
  '--color-warn-lit': 'rgb(255, 109, 90)',
  '--color-bg-warn': 'rgb(38, 11, 6)',
  '--color-border-warn': 'rgb(91, 22, 14)',

  // Caution
  '--color-caution': 'rgb(252, 214, 88)',
  '--color-caution-lit': 'rgba(252, 214, 88, 0.7)',
  '--color-bg-caution': 'rgba(252, 214, 88, 0.08)',
  '--color-bg-caution-subtle': 'rgba(252, 214, 88, 0.04)',
  '--color-border-caution': 'rgba(252, 214, 88, 0.15)',

  // Success
  '--color-success': 'rgb(0, 179, 170)', // Use accent for success too
  '--color-success-lit': 'rgb(4, 214, 203)',
  '--color-bg-success': 'rgb(0, 51, 48)',
  '--color-bg-success-subtle': 'rgb(0, 30, 28)',
  '--color-border-success': 'rgb(0, 88, 80)',

  // System messages
  '--color-bg-system': 'rgb(22, 26, 27)',
  '--color-border-system': 'rgb(49, 58, 59)',
  '--color-system': 'rgb(116, 116, 116)',

  // Shape — matches SAGE's border-radius tokens
  '--radius-xs': '4px',
  '--radius-md': '8px',
  '--radius-xl': '12px',

  // Animation
  '--animation-fast': '120ms',
  '--animation-normal': '220ms',
}
